import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import IconButton from '@/components/core/IconButton';
import Input from '@/components/core/Input';
import { IconMinus, IconPlus } from '@/icons';

interface TravellerClassSelectorProps {
  availableTravellerClasses: string[];
  tagTitles: Record<string, string>;
  quantities: Record<string, number>;
  allowMultiple: boolean;
  onChange: (quantities: Record<string, number>) => void;
}

export const TravellerClassSelector = ({
  availableTravellerClasses,
  tagTitles,
  quantities,
  allowMultiple,
  onChange,
}: TravellerClassSelectorProps) => {
  const { formatMessage } = useIntl();

  const handleStep = useCallback(
    (tagId: string, delta: number) => {
      const current = quantities[tagId] ?? 0;

      if (!allowMultiple) {
        if (delta > 0) {
          onChange({ [tagId]: 1 });
        } else {
          onChange({ ...quantities, [tagId]: Math.max(0, current - 1) });
        }
        return;
      }

      onChange({ ...quantities, [tagId]: Math.max(0, current + delta) });
    },
    [allowMultiple, quantities, onChange],
  );

  const handleInputChange = useCallback(
    (tagId: string, rawValue: string) => {
      const sanitized = rawValue.replace(/\D/g, '');
      const parsed = sanitized === '' ? 0 : parseInt(sanitized, 10);

      if (!allowMultiple) {
        onChange(parsed >= 1 ? { [tagId]: 1 } : { ...quantities, [tagId]: 0 });
        return;
      }

      onChange({ ...quantities, [tagId]: parsed });
    },
    [allowMultiple, quantities, onChange],
  );

  return (
    <div className="flex flex-col gap-2">
      <h3>
        <FormattedMessage defaultMessage="Antal resenärer" />
      </h3>
      <ul className="flex flex-col gap-2">
        {availableTravellerClasses.map((tagId) => {
          const quantity = quantities[tagId] ?? 0;
          const title = tagTitles[tagId] ?? tagId;
          const disableIncrease = !allowMultiple && quantity >= 1;

          return (
            <li
              key={tagId}
              className="flex items-center justify-between gap-4 rounded-md bg-background-100 px-4 py-2"
            >
              <span className="text-text-900">{title}</span>
              <div className="flex items-center gap-3">
                <IconButton
                  aria-label={formatMessage(
                    { defaultMessage: 'Minska antal {title}' },
                    { title },
                  )}
                  icon={<IconMinus />}
                  variant="outline"
                  round
                  disabled={quantity === 0}
                  onClick={() => handleStep(tagId, -1)}
                />
                <Input
                  type="text"
                  inputMode="numeric"
                  aria-label={formatMessage(
                    { defaultMessage: 'Antal {title}' },
                    { title },
                  )}
                  className="w-16 text-center tabular-nums"
                  value={String(quantity)}
                  onChange={(event) =>
                    handleInputChange(tagId, event.target.value)
                  }
                />
                <IconButton
                  aria-label={formatMessage(
                    { defaultMessage: 'Öka antal {title}' },
                    { title },
                  )}
                  icon={<IconPlus />}
                  variant="outline"
                  round
                  disabled={disableIncrease}
                  onClick={() => handleStep(tagId, 1)}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
